import { collection, query, where, orderBy, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Transaction, PaymentRequest } from '../types';

export interface UserLifetimeStats {
  totalEarned: number;
  totalWithdrawn: number;
  availableBalance: number;
  totalTransactions: number;
  totalPaymentRequests: number;
  firstTransactionDate?: Date;
  lastTransactionDate?: Date;
  monthlyStats: {
    [month: string]: {
      earned: number;
      withdrawn: number;
      transactions: number;
    };
  };
}

export interface TransactionHistory {
  transactions: Transaction[];
  paymentRequests: PaymentRequest[];
  lifetimeStats: UserLifetimeStats;
}

export class UserAnalyticsService {
  
  /**
   * Get comprehensive user lifetime statistics
   */
  async getUserLifetimeStats(userId: string): Promise<UserLifetimeStats> {
    try {
      // Get all transactions
      const transactionsQuery = query(
        collection(db, 'transactions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'asc')
      );
      
      const transactionsSnapshot = await getDocs(transactionsQuery);
      const transactions = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Transaction[];

      // Get all payment requests
      const paymentRequestsQuery = query(
        collection(db, 'paymentRequests'),
        where('userId', '==', userId),
        orderBy('createdAt', 'asc')
      );
      
      const paymentRequestsSnapshot = await getDocs(paymentRequestsQuery);
      const paymentRequests = paymentRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        processedAt: doc.data().processedAt?.toDate ? doc.data().processedAt.toDate() : undefined
      })) as PaymentRequest[];

      // Calculate lifetime stats
      const totalEarned = transactions
        .filter(t => t.type === 'cashback')
        .reduce((sum, t) => sum + t.amount, 0);

      const totalWithdrawn = transactions
        .filter(t => t.type === 'withdrawal')
        .reduce((sum, t) => sum + t.amount, 0);

      // Calculate available balance (earned - withdrawn - pending)
      const pendingAmount = paymentRequests
        .filter(req => req.status === 'pending')
        .reduce((sum, req) => sum + req.amount, 0);

      const availableBalance = Math.max(0, totalEarned - totalWithdrawn - pendingAmount);

      // Calculate monthly stats
      const monthlyStats: { [month: string]: { earned: number; withdrawn: number; transactions: number; } } = {};
      
      transactions.forEach(transaction => {
        const monthKey = transaction.createdAt.toISOString().slice(0, 7); // YYYY-MM format
        
        if (!monthlyStats[monthKey]) {
          monthlyStats[monthKey] = { earned: 0, withdrawn: 0, transactions: 0 };
        }
        
        if (transaction.type === 'cashback') {
          monthlyStats[monthKey].earned += transaction.amount;
        } else if (transaction.type === 'withdrawal') {
          monthlyStats[monthKey].withdrawn += transaction.amount;
        }
        
        monthlyStats[monthKey].transactions += 1;
      });

      // Get first and last transaction dates
      const sortedTransactions = transactions.sort((a, b) => 
        a.createdAt.getTime() - b.createdAt.getTime()
      );

      const firstTransactionDate = sortedTransactions.length > 0 ? sortedTransactions[0].createdAt : undefined;
      const lastTransactionDate = sortedTransactions.length > 0 ? sortedTransactions[sortedTransactions.length - 1].createdAt : undefined;

      return {
        totalEarned,
        totalWithdrawn,
        availableBalance,
        totalTransactions: transactions.length,
        totalPaymentRequests: paymentRequests.length,
        firstTransactionDate,
        lastTransactionDate,
        monthlyStats
      };
    } catch (error) {
      return {
        totalEarned: 0,
        totalWithdrawn: 0,
        availableBalance: 0,
        totalTransactions: 0,
        totalPaymentRequests: 0,
        monthlyStats: {}
      };
    }
  }

  /**
   * Get complete transaction history with proper categorization
   */
  async getTransactionHistory(userId: string): Promise<TransactionHistory> {
    try {
      // Get all transactions
      const transactionsQuery = query(
        collection(db, 'transactions'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const transactionsSnapshot = await getDocs(transactionsQuery);
      
      const transactions = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      })) as Transaction[];

      // Get all payment requests
      const paymentRequestsQuery = query(
        collection(db, 'paymentRequests'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const paymentRequestsSnapshot = await getDocs(paymentRequestsQuery);
      
      const paymentRequests = paymentRequestsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        processedAt: doc.data().processedAt?.toDate ? doc.data().processedAt.toDate() : undefined
      })) as PaymentRequest[];

      // transactionId is now stored directly in paymentRequest document when approved
      // No need to query transactions collection (which requires permissions)
      // Just use the transactionId from the paymentRequest document itself
      const paymentRequestsWithTransactionId = paymentRequests.map(req => ({
        ...req,
        transactionId: req.transactionId || undefined // transactionId is already in paymentRequest document
      })) as PaymentRequest[];

      // Get lifetime stats
      const lifetimeStats = await this.getUserLifetimeStats(userId);

      return {
        transactions,
        paymentRequests: paymentRequestsWithTransactionId,
        lifetimeStats
      };
    } catch (error) {
      return {
        transactions: [],
        paymentRequests: [],
        lifetimeStats: {
          totalEarned: 0,
          totalWithdrawn: 0,
          availableBalance: 0,
          totalTransactions: 0,
          totalPaymentRequests: 0,
          monthlyStats: {}
        }
      };
    }
  }

  /**
   * Get user's current balance from Firestore
   */
  async getUserCurrentBalance(userId: string): Promise<number> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.cashbackValue || 0;
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Get monthly breakdown for charts
   */
  async getMonthlyBreakdown(userId: string): Promise<{ month: string; earned: number; withdrawn: number; }[]> {
    try {
      const lifetimeStats = await this.getUserLifetimeStats(userId);
      const monthlyData = Object.entries(lifetimeStats.monthlyStats).map(([month, stats]) => ({
        month,
        earned: stats.earned,
        withdrawn: stats.withdrawn
      }));
      
      return monthlyData.sort((a, b) => a.month.localeCompare(b.month));
    } catch (error) {
      return [];
    }
  }
}

// Singleton instance
export const userAnalytics = new UserAnalyticsService(); 